import { useTranslation } from 'react-i18next';
import { Box, SvgIcon, Theme, useMediaQuery } from '@mui/material';
import { Button, ConditionalTooltip } from '@atoms';
import { useUser } from '@hooks';
import { CustomStyles } from '@types';
import {
  employeeDetailsClick,
  EVENT_EMPLOYEE_DETAILS_SELECT_PRODUCT,
} from '@modules/employeeDetails/events';
import { Product } from '@modules/employeeDetails/tabs/productsTab/useProducts';
import styles from './styles';
import FeaturesList from '../FeaturesList';

interface ItemInterface {
  title: string;
  icon: React.ReactNode;
  account_number: string;
  onClick: () => void;
  [key: string]: unknown;
}
interface TabSelectorProps {
  items: ItemInterface[];
  selectedTab: Product;
  setSelectedTab: (tab: Product) => void;
  keyProp: string;
  titleProp: string;
  customStyle: {
    text?: CustomStyles;
    button?: CustomStyles;
    icon?: CustomStyles;
  };
  rightButton: {
    text: string;
    onClick: () => void;
    icon: React.ReactNode;
  };
  features?: string[] | null;
  openSidePanel: () => void;
}

/**
 * ProductTabSelector
 * @component
 * @param props
 * @param props.items options of the tab selector
 * @param props.selectedTab
 * @param props.keyProp the prop to use for key
 * @param props.titleProp the prop to show in the title
 * @param props.customStyle
 * @param props.features
 * @param props.openSidePanel
 * @param props.rightButton
 * @param props.rightButton.text
 * @param props.rightButton.onClick
 * @param props.rightButton.icon
 */
const ProductTabSelector = ({
  items,
  selectedTab,
  keyProp,
  titleProp,
  customStyle = {},
  rightButton,
  features,
  openSidePanel,
}: TabSelectorProps) => {
  const responsive = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { user } = useUser();
  const { t } = useTranslation();
  const canViewEnabledFeatures = user?.permissions?.includes('view:enabled_features');
  const canViewEnabledBanners = user?.permissions?.includes('view:enabled_banners');
  const combinedButtonStyles = {
    button: {
      ...styles.button.button,
      ...customStyle.button,
    },
    text: {
      ...styles.button.text,
      ...customStyle.text,
    },
    icon: {
      ...styles.button.icon,
      ...customStyle.icon,
    },
  };

  return (
    <Box sx={styles.container(responsive)}>
      <Box sx={styles.rowContainer}>
        <Box sx={styles.containerResponsive(responsive)}>
          {items?.map((item, index) => {
            const titleValue = item[titleProp as keyof ItemInterface];
            const text = Array.isArray(titleValue) ? titleValue.join(', ') : String(titleValue);
            const isSelected = selectedTab.account_number === item.account_number;
            const keyValue = item[keyProp as keyof ItemInterface];
            return (
              <ConditionalTooltip tooltipText={text} key={index}>
                <Button
                  key={String(keyValue)}
                  variant={isSelected ? 'outlined' : 'text'}
                  startIcon={<SvgIcon sx={styles.button.icon(isSelected)}>{item.icon}</SvgIcon>}
                  text={text}
                  onClick={() => {
                    employeeDetailsClick(EVENT_EMPLOYEE_DETAILS_SELECT_PRODUCT);
                    item.onClick();
                  }}
                  customStyle={combinedButtonStyles}
                />
              </ConditionalTooltip>
            );
          })}
        </Box>
        {rightButton && (
          <Box sx={styles.rightButton}>
            <Button
              startIcon={<SvgIcon sx={styles.button.icon(false)}>{rightButton.icon}</SvgIcon>}
              text={rightButton.text}
              onClick={() => rightButton.onClick()}
              customStyle={combinedButtonStyles}
            />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          ...styles.rowFooterContainer,
          ...(canViewEnabledBanners &&
            !canViewEnabledFeatures && {
              justifyContent: 'flex-end',
            }),
        }}
      >
        {canViewEnabledFeatures && (
          <Box sx={{ width: '100%' }}>
            <FeaturesList features={features} />
          </Box>
        )}
        {canViewEnabledBanners && (
          <Box sx={styles.rightButtonBanner}>
            <Button
              text={t('EMPLOYEES:DETAIL:VIEW_BANNERS')}
              onClick={openSidePanel}
              customStyle={combinedButtonStyles}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProductTabSelector;
